// API-слой тула «Анонсы для соцсетей» (social_announcer).
//
// Схема транспорта — та же, что у генератора обложек (см. api/cover.ts):
// ответ generate — это МАССИВ вариантов, а не одиночный объект. Один POST
// создаёт N независимых генераций (двойная генерация BP-010: ПО ДВЕ на каждый
// отмеченный режим), каждая поллится отдельно через GET /api/tasks/{id}/.
//
// Отличий от обложек ровно два:
//   1. Путь эндпоинта (/api/tools/social_announcer/generate/).
//   2. В payload — список слагов режимов `modes` вместо пары `model` + `count`,
//      а каждый вариант ответа дополнительно несёт код своего режима `mode`.
//
// Зачем варианту `mode`: у обложек и баннеров блок результата подписывается
// безлико («Вариант 1 из 3»), потому что варианты равнозначны. Здесь тексты
// РАЗНЫЕ по смыслу, и редактор должен видеть, какой из них какой. Сопоставлять
// режим по индексу массива (i-й вариант ↔ i-й отмеченный чекбокс) нельзя: при
// частичном отказе или перестановке на бэкенде подписи разъедутся и текст без
// спойлеров окажется под шапкой «Информативный». Поэтому код режима
// приходит явно в каждом элементе variants[].
//
// Idempotency-Key — ОДИН на весь батч: повтор тем же ключом за 24ч вернёт тот
// же набор variants (idempotent_replay:true), не плодя дубли.
import { apiClient, getNormalized } from "./client";
import { generateIdempotencyKey } from "./idempotency";
import type { GenerationStatus } from "./types";
import type { AnnouncerMode } from "@/pages/tools/SocialAnnouncer/announcerConfig";
import {
  ANNOUNCER_COPY_MARK_RETRY_DELAY_MS,
  ANNOUNCER_SUBMIT_TIMEOUT_MS,
} from "@/pages/tools/SocialAnnouncer/announcerConfig";

export interface AnnouncerGeneratePayload {
  /** Заголовок публикации, для которой делается анонс. */
  headline: string;
  /** Основной текст публикации. */
  body: string;
  /** Пожелания редактора к тону и акцентам анонса. */
  wishes: string;
  /** 1..2 слага режимов (в реестре их ровно два); порядок — порядок, в котором
   *  редактор отмечал чекбоксы (НЕ порядок реестра announcerConfig): группа
   *  чекбоксов antd в режиме children сортирует по массиву `options`, а он пуст,
   *  поэтому порядок клика доезжает до payload как есть. Бэкенд дедуплицирует
   *  `modes` с сохранением присланного порядка — значит именно порядок
   *  отметки определяет порядок карточек результата. Подписи карточек это не
   *  задевает: они резолвятся по полю `mode` каждого варианта, а не по
   *  позиции в массиве. */
  modes: AnnouncerMode[];
}

/** Один вариант в ответе батча — один режим анонса, двойная генерация (BP-010):
 *  два варианта на режим, различаются `slot` (1 или 2). `slot: null` — старая
 *  запись до двойной генерации (одиночный режим без слота). */
export interface AnnouncerVariant {
  task_id: string;
  generation_id: string;
  /** Код режима — нужен, чтобы подписать блок результата. */
  mode: AnnouncerMode;
  /** Номер варианта внутри режима. Модель, стоящая за слотом, редактору не
   *  показывается никогда (§0 п. 5 спецификации) — слот НЕ раскрывает модель. */
  slot: 1 | 2 | null;
}

/** Ответ generate-эндпоинта (массив вариантов, по два на режим). */
export interface AnnouncerGenerateResponse {
  count: number;
  status: GenerationStatus;
  variants: AnnouncerVariant[];
  idempotent_replay: boolean;
}

/** Запуск генерации анонсов. Idempotency-Key — один на весь батч.
 *
 *  Запрос ТИХИЙ (`silentErrors: true`): страница сама показывает РОВНО ОДИН
 *  тост об ошибке запуска в `onError` мутации (§8.6 спецификации) — без этого
 *  флага редактор увидел бы два сообщения (глобальный тост interceptor'а и
 *  тост страницы). Точечный таймаут — свой (60 с), глобальные 30 с
 *  (api/constants.ts) не трогаем: двойная генерация создаёт вдвое больше
 *  записей за один запрос, и отправка батча может занять больше обычного. */
export async function generateAnnounce(
  payload: AnnouncerGeneratePayload,
  idempotencyKey: string,
): Promise<AnnouncerGenerateResponse> {
  const { data } = await apiClient.post<AnnouncerGenerateResponse>(
    "/api/tools/social_announcer/generate/",
    payload,
    {
      headers: { "Idempotency-Key": idempotencyKey },
      timeout: ANNOUNCER_SUBMIT_TIMEOUT_MS,
      silentErrors: true,
    },
  );
  return data;
}

/** Ответ повтора одного варианта — тот же контракт, что у варианта батча,
 *  плюс статус повтора и ссылка на замененную запись. */
export interface AnnouncerRetryResponse extends AnnouncerVariant {
  status: GenerationStatus;
  retry_of: string | null;
  idempotent_replay: boolean;
}

/** Платный повтор ТОЛЬКО одного упавшего или зависшего варианта — не всего
 *  батча. Тихий: блок результата сам показывает текст ошибки под кнопкой
 *  «Повторить» (TextVariantResultBlock, §8.5), глобальный тост не нужен. */
export async function retryAnnouncerVariant(
  generationId: string,
): Promise<AnnouncerRetryResponse> {
  const { data } = await apiClient.post<AnnouncerRetryResponse>(
    `/api/tools/social_announcer/variants/${generationId}/retry/`,
    undefined,
    { silentErrors: true },
  );
  return data;
}

export interface AnnouncerCopyMarkPayload {
  /** Был ли на экране виден готовый сосед (тот же режим, другой слот) в
   *  момент клика «Копировать». null — сосед неизвестен (старая запись без
   *  batch_id) либо сравнивать не с чем. */
  sibling_visible: boolean | null;
  /** UUID клиентского события — сервер не задваивает счётчик при повторной
   *  отправке той же отметки (сетевой обрыв + автоповтор). */
  event_id: string;
}

/** Отметка «копировался». Тихая: неудача отметки не должна мешать редактору —
 *  текст уже скопирован в буфер, ошибка отметки не должна порождать тост. */
export async function markAnnouncerVariantCopied(
  generationId: string,
  payload: AnnouncerCopyMarkPayload,
): Promise<void> {
  await apiClient.post(
    `/api/tools/social_announcer/variants/${generationId}/copied/`,
    payload,
    { silentErrors: true },
  );
}

/** Отметка «копировался» без тостов: один отложенный повтор при 429 / сети /
 *  5xx (те же коды, что считаются «сервер мог не увидеть запрос»). Возвращает
 *  true, если отметка в итоге записана. НИКОГДА не бросает исключение — сбой
 *  отметки не должен ломать уже выполненное копирование в буфер обмена.
 *
 *  `deps.sleep` — шов для тестов (не спать по-настоящему). */
export async function reportVariantCopied(
  generationId: string,
  payload: AnnouncerCopyMarkPayload,
  deps: { sleep?: (ms: number) => Promise<void> } = {},
): Promise<boolean> {
  const sleep = deps.sleep ?? ((ms: number) => new Promise((resolve) => setTimeout(resolve, ms)));

  try {
    await markAnnouncerVariantCopied(generationId, payload);
    return true;
  } catch (err) {
    const normalized = getNormalized(err);
    const retryable =
      normalized.status === 0 || normalized.status === 429 || normalized.status >= 500;
    if (!retryable) return false;

    // Окно троттлинга 60 с — при 429 не ждём дольше, даже если сервер
    // подсказал больше: вторая попытка всё равно должна успеть.
    const delayMs =
      normalized.status === 429
        ? Math.min((normalized.retry_after_seconds ?? 3) * 1000, 60_000)
        : ANNOUNCER_COPY_MARK_RETRY_DELAY_MS;
    await sleep(delayMs);

    try {
      // Тот же event_id — сервер не задвоит счётчик при повторной отметке.
      await markAnnouncerVariantCopied(generationId, payload);
      return true;
    } catch (retryErr) {
      // Сбой отметки не критичен (текст анонса уже скопирован в буфер), но
      // диагностика полезна. В консоль — ТОЛЬКО статус и сообщение, а не весь
      // объект axios-ошибки: там `config` с заголовками (включая
      // X-CSRFToken) и `request` — лишнее в консоли, даже без текста анонса.
      const { status, message: errMessage } = getNormalized(retryErr);
      // eslint-disable-next-line no-console
      console.warn("reportVariantCopied: отметка не записана после повтора", {
        status,
        message: errMessage,
      });
      return false;
    }
  }
}

export { generateIdempotencyKey };
